'use client';

import { c as _c } from "react/compiler-runtime";
import type { CommandWorkflowGroup, CommandWorkflowPage } from '@repo/shortcuts/command';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
type CommandRegistration = {
  register: (registration: PageCommandRegistration) => void;
  unregister: (id: string) => void;
};
type CommandRegistryContextValue = CommandRegistration & {
  registrations: PageCommandRegistration[];
};
type PageCommandRegistration = {
  groups?: CommandWorkflowGroup[];
  id: string;
  nestedPages?: Record<string, CommandWorkflowPage>;
};
const CommandRegistryContext = createContext<CommandRegistryContextValue | null>(null);
const CommandRegistrationContext = createContext<CommandRegistration | null>(null);
export const CommandRegistryProvider = t0 => {
  const $ = _c(11);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [registrations, setRegistrations] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => ({
      register: next => setRegistrations(prev => [...prev.filter(r => r.id !== next.id), next]),
      unregister: id => setRegistrations(prev_0 => prev_0.filter(r_0 => r_0.id !== id))
    });
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [registration] = useState(t2);
  let t3;
  if ($[2] !== registration || $[3] !== registrations) {
    t3 = {
      ...registration,
      registrations
    };
    $[2] = registration;
    $[3] = registrations;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const value = t3;
  let t4;
  if ($[5] !== children || $[6] !== value) {
    t4 = <CommandRegistryContext.Provider value={value} data-sentry-element="CommandRegistryContext.Provider" data-sentry-source-file="command-provider.tsx">{children}</CommandRegistryContext.Provider>;
    $[5] = children;
    $[6] = value;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== registration || $[9] !== t4) {
    t5 = <CommandRegistrationContext value={registration} data-sentry-element="CommandRegistrationContext" data-sentry-component="CommandRegistryProvider" data-sentry-source-file="command-provider.tsx">{t4}</CommandRegistrationContext>;
    $[8] = registration;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};
export const useCommandRegistry = () => {
  const context = useContext(CommandRegistryContext);
  if (!context) {
    throw new Error("useCommandRegistry must be used within CommandRegistryProvider");
  }
  return context;
};
export const usePageCommands = (id, groups, nestedPages) => {
  const $ = _c(7);
  const registration = useContext(CommandRegistrationContext);
  if (!registration) {
    throw new Error("usePageCommands must be used within CommandRegistryProvider");
  }
  const {
    register,
    unregister
  } = registration;
  let t0;
  let t1;
  if ($[0] !== groups || $[1] !== id || $[2] !== nestedPages || $[3] !== register || $[4] !== unregister) {
    t0 = () => {
      register({
        groups,
        id,
        nestedPages
      });
      return () => {
        unregister(id);
      };
    };
    t1 = [id, groups, nestedPages, register, unregister];
    $[0] = groups;
    $[1] = id;
    $[2] = nestedPages;
    $[3] = register;
    $[4] = unregister;
    $[5] = t0;
    $[6] = t1;
  } else {
    t0 = $[5];
    t1 = $[6];
  }
  useEffect(t0, t1);
};